import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseCreateDailySubContractorMutation, UseDailySubContractorMutation } from '../../../../hooks/mutations/useDailySubContractorMutation';
import { useSubContractorsQuery } from '../../../../hooks/queries/useSubContractorsQuery';
import { DailySubContractor } from '../../../../services/apiSLTP/planning';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { getMaterialStateInfos, materialState, systemConfigKeyEnum } from '../../../../utils/enums';
import DropDown, { OptionType } from '../../../DropDown';
import ToggleLine from '../../../ToggleLine';
import ModalBody, { ModalProps } from '../../ModalBody';
import CustomTextInput from '../../../CustomTextInput';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';

interface ManageValidationDailySubContractorModalProps extends ModalProps {
    refetch: () => void;
    dailySubContractor: DailySubContractor | null;
    activities: OptionType[];
    siteName?: string;
}

const ManageValidationDailySubContractorModal: React.FC<ManageValidationDailySubContractorModalProps> = ({
    open,
    handleClose,
    refetch,
    dailySubContractor,
    activities,
    siteName,
}) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const [currentDailySubContractorId] = useState(dailySubContractor?._id || '');
    const [selectedSubContractorType, setSelectedSubContractorType] = useState({
        label: dailySubContractor?.subContractorTypeId.designation || '',
        value: dailySubContractor?.subContractorTypeId._id || '',
    });
    const [selectedActivity, setSelectedActivity] = useState({
        label: activities.find((activity) => activity.value === dailySubContractor?.activityId)?.label || '',
        value: dailySubContractor?.activityId || '',
    });
    const [includedInExpenses, setIncludedInExpenses] = useState<boolean>(dailySubContractor?.includedInExpenses ?? true);
    const [price, setPrice] = useState(dailySubContractor?.price?.toString() || '');

    const { data: dataSubContractors } = useSubContractorsQuery({ queries: { active: true } });

    const { mutate: createDailySubContractor } = UseCreateDailySubContractorMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });
    const { mutate: updateDailySubContractor } = UseDailySubContractorMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });

    const postDailySubContractor = async () => {
        createDailySubContractor({
            body: {
                activityId: selectedActivity.value,
                subContractorTypeId: selectedSubContractorType.value,
                includedInExpenses,
                price: price.length ? parseFloat(price) : undefined,
            },
        });
    };

    const patchDailySubContractor = async () => {
        if (currentDailySubContractorId)
            updateDailySubContractor({
                id: currentDailySubContractorId,
                body: {
                    activityId: selectedActivity.value,
                    subContractorTypeId: selectedSubContractorType.value,
                    includedInExpenses,
                    price: price.length ? parseFloat(price) : undefined,
                },
            });
    };

    return (
        <ModalBody
            closeText={t('cancel')}
            title={t('subContractor')}
            subTitle={!!currentDailySubContractorId.length ? t('update') : t('add')}
            description={siteName}
            okText={t('validate')}
            open={open}
            handleClose={handleClose}
            handleOk={() => {
                !currentDailySubContractorId.length ? postDailySubContractor() : patchDailySubContractor();
            }}
            disabledOk={!selectedSubContractorType.value.length || !selectedActivity.value.length}
        >
            <DropDown
                selectedItem={selectedActivity}
                items={activities}
                handleChange={(event, value) => setSelectedActivity({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('activityOf')}
                required
            />
            <DropDown
                selectedItem={selectedSubContractorType}
                items={
                    dataSubContractors?.map((subContractor) => ({
                        label: subContractor.designation,
                        value: subContractor._id,
                    })) || []
                }
                handleChange={(event, value) => setSelectedSubContractorType({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('company')}
                required
            />

            <ToggleLine
                checked={includedInExpenses}
                onChange={(checked) => setIncludedInExpenses(checked)}
                title={capitalizeFirstLetter(t('includedInExpenses'))}
            />
            {includedInExpenses && (
                <CustomTextInput
                    value={price}
                    onChange={setPrice}
                    label={t('price')}
                    startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                    type="number"
                />
            )}
        </ModalBody>
    );
};

export default ManageValidationDailySubContractorModal;
